import Step from "./Step"
import React, { useState, useEffect } from "react"
import Button from "../../Components/Button"
import { Field } from "formik"
import * as Yup from "yup"
import FormikField from "../../Components/Forms/FormikField"
import ListTextPreview from "../../Components/ListTextPreview"
import GoalBlock from "../../Components/GoalBlock"
import { useAtom } from "jotai"
import { roleAtom, userAtom, planTokenAtom, planAtom } from "../../state"
import { getStepMetadata } from "../../constants/stepRegistry"
import { PLAN_USER_ROLE } from "../../constants/planUserRole"
import api from "../../services/api"
import { COMMITMENTS_RESOURCE_URL } from "../../constants/resources"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"

const stepMetadata = getStepMetadata("commitments")

const shape = {
  commitment: Yup.string().required("Required"),
}
const schema = Yup.object().shape(shape)

const step = {
  name: stepMetadata.name,
  title: "Make a commitment that matters.",
  type: stepMetadata.type,
  instructions: [
    "Share 1-2 commitments for how you can support your student's goals in the year ahead. You're not promising to be perfect but you're promising to be present. It is important that these are promises you know you can keep.",
    "Take a few minutes to reflect and then share with your student.",
    <MoreInfoVideoModal
      src={COMMITMENTS_RESOURCE_URL}
      type={"pdf"}
      postText={"Commitment Examples"}
      icon={"fa-regular fa-file-pen"}
      title={"Commitment Examples"}
      text={""}
      textClassName={"text-commitments no-underline"}
    />,
  ],
  discussionQuestions: [
    "What is one specific way I can support this student over the next year?",
    "How can I encourage their faith, their goals, or their sense of identity?",
    "What will I do-how often, when, or where-that will make this student feel seen and supported?",
  ],
}

function Commitments() {
  const [user] = useAtom(userAtom)
  const [role] = useAtom(roleAtom)
  const [planToken] = useAtom(planTokenAtom)
  const [plan] = useAtom(planAtom)
  const [mentors, setMentors] = useState([])
  const [selectedMentorId, setSelectedMentorId] = useState(null)

  useEffect(() => {
    if (planToken) {
      api.getMentors(planToken).then(response => {
        setMentors(response.data.mentors || [])
      })
    }
  }, [planToken])

  const getPreviewData = (data, filterMentorId = null) => {
    if (filterMentorId) {
      return data.commitments.filter(item => item.user_id === filterMentorId)
    }
    return data.commitments
  }
  const getIsComplete = data => {
    if (role == PLAN_USER_ROLE.YOUTH || role == PLAN_USER_ROLE.GUIDE) {
      return data.commitments.length > 0
    }
    return data.commitments.filter(item => item.user_id == user.id).length > 0
  }
  const onSubmit = (values, actions, onSubmit) => {
    onSubmit({ commitments: [values.commitment] }, false, false)
    actions.resetForm()
  }
  return (
    <Step
      step={step}
      Preview={ListTextPreview}
      getIsComplete={getIsComplete}
      getPreviewData={getPreviewData}
      previewType="single"
      keys={[
        "commitments",
        "visions",
        "other_title",
        "faith_vision",
        "family_vision",
        "sports_vision",
        "school_vision",
        "arts_music_vision",
        "travel_vision",
        "hobbies_vision",
        "service_vision",
        "other_vision",
        "relationships_vision",
        "work_vision",
        "justice_vision",
      ]}
      schema={schema}
      image={stepMetadata.image}
      theme={stepMetadata.theme}
      onSubmit={onSubmit}
      description={stepMetadata.description}
    >
      {(values, data) => (
        <>
          <FormikField
            primary={true}
            name="commitment"
            instructions={step.instructions}
            title={step.title}
          >
            <div className="mb-4">
              <h3 className="mb-2 text-lg font-bold">Reflection Prompts</h3>
              <p className="mb-2">Use these questions to guide your commitment.</p>
              <ul className="mb-4 list-inside list-disc">
                {step.discussionQuestions.map((question, index) => (
                  <li className={"font-bold"} key={index}>
                    {question}
                  </li>
                ))}
              </ul>
            </div>

            <GoalBlock data={data} plan={plan} />

            <>
              <div className="mb-4 text-sm">Share 1 or 2 Commitments below</div>
              <ListTextPreview
                data={getPreviewData(data)}
                editable={true}
                theme="commitments"
                heading="Mentor Commitments"
              />

              <div className="mb-2 flex items-center gap-4">
                <Field
                  as="textarea"
                  key={data.commitments.length + 1}
                  name="commitment"
                  placeholder="I commit to..."
                  className="h-32 w-full rounded-md border p-2"
                />
              </div>
              <Button bgColor="bg-commitments hover:bg-commitments/90 mb-8" type="submit">
                Add Commitment
              </Button>
            </>
          </FormikField>
        </>
      )}
    </Step>
  )
}

export default Commitments
